import { Spacing } from '@persona-id/design-tokens';
import { ComponentProps, useCallback, useState } from 'react';
import styled from 'styled-components';

import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import { ComponentTranslationsSchema } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { SCREEN_EDITOR_CONTROL_PANEL_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import TranslationEditSection from 'dashboard/apps/InquiryTemplateEditor/TranslationEditSection/TranslationEditSection';
import TranslationsPanel from 'dashboard/apps/InquiryTemplateEditor/TranslationsPanel';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import useToggle from 'lib/hooks/useToggle';
import { UiComponent, UiTextComponent } from 'lib/interfaces/ui-components';

export const TranslationEditSectionContainer = styled.div`
  &:not(:last-child) {
    margin-bottom: ${Spacing[5]};
  }
`;

export interface TranslationsConfig {
  path: string;
  stepName: string;
  name: string;
  clientKey: string;
  defaultValue: string;
  translationsKey: string;
}

export interface TranslationsEditorProps {
  componentName: string;
  enabledLocales: InquiryIso6391LanguageCode[];
  componentPreview: UiTextComponent;
  offsetElementId?: string;
  basePath: string;
  currentLocale: InquiryIso6391LanguageCode;
  editable: boolean;
  stepName: string;
  translationsSchema: ComponentTranslationsSchema[];
  getStepLocalizedStrings: (locales?: Array<InquiryIso6391LanguageCode>) => Promise<void>;
  handleTranslationUpdates: (componentConfigUpdates: Record<string, any>) => void;
  disabledWarning?: string;
}

const TranslationsEditor = ({
  componentName,
  basePath,
  offsetElementId = SCREEN_EDITOR_CONTROL_PANEL_ID,
  currentLocale,
  enabledLocales,
  componentPreview,
  stepName,
  translationsSchema,
  editable,
  getStepLocalizedStrings,
  handleTranslationUpdates,
  disabledWarning,
}: TranslationsEditorProps) => {
  const [translationsConfig, setTranslationsConfig] = useState<TranslationsConfig | undefined>(
    undefined,
  );

  const [
    showTranslationsPanel,
    { enable: openTranslationsPanel, disable: closeTranslationsPanel },
  ] = useToggle(false);

  const handleOpenTranslationsPanel = useCallback(
    (config: TranslationsConfig) => {
      setTranslationsConfig(config);
      openTranslationsPanel();
    },
    [openTranslationsPanel, setTranslationsConfig],
  );

  const sectionConfigs: Pick<ComponentProps<typeof TranslationEditSection>, 'config' | 'label'>[] =
    translationsSchema.map((schema) => {
      const { label, 'client-key': clientKey, 'custom-translations-key': translationsKey } = schema;
      return {
        label: label,
        config: {
          path: `${basePath}.config.${translationsKey}`,
          translationsKey,
          stepName: stepName,
          name: componentName,
          clientKey: clientKey,
          defaultValue: componentPreview.attributes[clientKey as keyof UiComponent['attributes']],
        },
      };
    });

  return (
    <SectionAccordion title='Translations'>
      {sectionConfigs?.map(({ config, label }, idx) => (
        <TranslationEditSectionContainer
          key={`translation-editor-${config.name}-${currentLocale}-section-${idx}`}
        >
          <TranslationEditSection
            config={config}
            label={label}
            enabledLocales={enabledLocales}
            currentLocale={currentLocale}
            editable={editable}
            handleOpenPanel={(config: TranslationsConfig) => handleOpenTranslationsPanel(config)}
            getStepLocalizedStrings={getStepLocalizedStrings}
            handleTranslationUpdates={handleTranslationUpdates}
            disabledWarning={disabledWarning}
          />
        </TranslationEditSectionContainer>
      ))}
      {showTranslationsPanel && translationsConfig && (
        <TranslationsPanel
          offsetElementId={offsetElementId}
          config={translationsConfig}
          currentLocale={currentLocale}
          enabledLocales={enabledLocales}
          editable={editable}
          getStepLocalizedStrings={getStepLocalizedStrings}
          handleTranslationUpdates={handleTranslationUpdates}
          onOutsideClick={closeTranslationsPanel}
        />
      )}
    </SectionAccordion>
  );
};

export default TranslationsEditor;
